import { useConfigurationTheme } from '@gitbook/react-uikit';
import { createCache } from '@gitbook/util-suspense-cache';
import { assertIsDefined } from '@gitbook/util-ts';

import { excalidraw } from '../excalidraw';
import { createDrawingSVGElement, type DrawingDocumentData } from './files';

/**
 * Cache to read a drawing document from a file URL.
 */
export const drawingDataCache = createCache<string | null, DrawingDocumentData>({
    getKey: (downloadURL) => downloadURL || '',
    getValue: async (downloadURL) => {
        if (!downloadURL) {
            return { elements: [], files: {} };
        }

        const {
            default: { loadFromBlob },
        } = await excalidraw();

        const res = await fetch(downloadURL);
        const blob = await res.blob();

        const result = await loadFromBlob(blob, null, null);

        return {
            elements: result.elements,
            files: result.files,
        };
    },
});

/**
 * Generate a themed SVG dom element from a drawing file URL.
 * This SVG should be used for rendering but not for saving.
 */
const drawingSVGCache = createCache<
    { downloadURL: string; theme: 'light' | 'dark' },
    SVGSVGElement | undefined
>({
    getKey: ({ downloadURL, theme }) => `${downloadURL}-${theme}`,
    getValue: async ({ downloadURL, theme }) => {
        const doc = await drawingDataCache.readAsync(downloadURL);

        const svg = await createDrawingSVGElement(doc, {
            embedScene: false,
            theme,
        });

        // Normalize the size to adapt to the container
        svg.style.maxWidth = '100%';
        svg.removeAttribute('width');
        svg.removeAttribute('height');

        return svg;
    },
});

/**
 * Hook to read a themed SVG dom element from a drawing file URL.
 */
export function useDrawingSVGElement(downloadURL: string, themeOverride?: 'light' | 'dark') {
    const theme = useConfigurationTheme();
    const svg = drawingSVGCache.read({
        downloadURL,
        theme: themeOverride ?? theme.color,
    });
    assertIsDefined(svg);
    return svg;
}
